import {
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  KeyRound,
  LogOut,
  MailCheck,
  MoreHorizontal,
  PauseCircle,
  PlayCircle,
  RotateCw,
  Trash2,
  UserCheck,
  UserX,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { HANDOVER_KINDS, HANDOVER_KIND_LABELS } from '@nest-recruit/shared';
import { Avatar } from '../../../../components/Avatar';
import { Button } from '../../../../components/Button';
import { Field } from '../../../../components/Field';
import { Input } from '../../../../components/Input';
import { MenuItem, Popover, usePopover } from '../../../../components/Popover';
import { SkeletonLines } from '../../../../components/Skeleton';
import { ServerErrorState } from '../../../../components/system-states/SystemState';
import { Switch } from '../../../../components/Switch';
import { ToneChip } from '../../../../components/Tone';
import { EMPTY, formatDateTime, formatNumber, orEmpty } from '../../../../lib/format';
import { useCurrentSession } from '../../../../stores/session';
import { useToasts } from '../../../../stores/toasts';
import { useStartImpersonation } from '../../../auth/api/auth-api';
import {
  useReactivateUser,
  useResendInvite,
  useRevokeInvite,
  useRevokeUserSession,
  useSendPasswordReset,
  useSuspendUser,
  useUnlockUser,
  useUnsuspendUser,
  useUpdateUser,
  useUser,
  type UserDetail,
} from '../../api/users-api';
import { AvailabilityEditor } from '../AvailabilityEditor';
import { RoleAssignmentEditor } from '../RoleAssignmentEditor';
import { WorkCalendarOverride } from '../WorkCalendarOverride';
import { DeactivateConfirm } from './DeactivateConfirm';
import { PaneEmbed, PaneFrame, PaneSection, type PaneMode } from './PaneFrame';
import { PEOPLE_STATUS_LABELS, PEOPLE_STATUS_TONES, describeAgent, firstNameOf, isLocked, listStatusOf, lockLine } from './people-format';
import styles from './PersonPane.module.css';

/** How many sessions show before "Show all". */
const SESSIONS_SHOWN = 5;

export interface PaneNavigation {
  /** "6 of 142", or null when the open person isn't on this page of the list (a deep link). */
  readonly position: { readonly index: number; readonly total: number } | null;
  readonly onPrevious: (() => void) | null;
  readonly onNext: (() => void) | null;
}

/**
 * USER-02 in the pane beside People (Admin redesign 2026-11-02, People A): one person, and everything that can be
 * done to them, without leaving the list. Every function of the old full page is here — unlock, view as,
 * reactivate, deactivate with the hand-over, roles and reach, the name, the panel switch, the working week, free
 * times, what they own, their sessions and their pending invite — plus Suspend and Reset password.
 *
 * Self-guards: nobody deactivates, suspends or views as themselves (the server refuses them too).
 */
export function PersonPane({
  userId,
  canEdit,
  mode,
  navigation,
  onClose,
}: {
  readonly userId: string;
  readonly canEdit: boolean;
  readonly mode: PaneMode;
  readonly navigation: PaneNavigation;
  readonly onClose: () => void;
}): ReactNode {
  const user = useUser(userId);
  // Review R29: only a 404 means they don't exist; any other failure is ours, and can be tried again.
  const isMissing = user.isError && user.error.status === 404;
  const title = user.data?.fullName ?? (user.isPending ? 'Loading' : isMissing ? 'Not found' : "Couldn't load");

  return (
    <PaneFrame
      mode={mode}
      label="Person"
      title={title}
      {...(user.data ? { sub: user.data.email, leading: <Avatar name={user.data.fullName} size="md" /> } : {})}
      nav={
        <>
          {navigation.position && (
            <span className={styles.position}>
              {formatNumber(navigation.position.index)} of {formatNumber(navigation.position.total)}
            </span>
          )}
          <Button
            variant="ghost"
            size="sm"
            icon={ChevronLeft}
            aria-label="Previous person"
            title="Previous person (↑)"
            disabled={navigation.onPrevious === null}
            onClick={() => navigation.onPrevious?.()}
          />
          <Button
            variant="ghost"
            size="sm"
            icon={ChevronRight}
            aria-label="Next person"
            title="Next person (↓)"
            disabled={navigation.onNext === null}
            onClick={() => navigation.onNext?.()}
          />
        </>
      }
      onClose={onClose}
    >
      {user.isPending ? (
        <PaneSection label="Loading">
          <SkeletonLines count={8} />
        </PaneSection>
      ) : user.data ? (
        // Keyed by the person: a form half-typed for one person never carries over to the next.
        <PersonBody key={user.data.id} user={user.data} canEdit={canEdit} />
      ) : user.isError && !isMissing ? (
        <PaneSection label="Couldn't load">
          <ServerErrorState requestId={user.error.requestId} onRetry={() => void user.refetch()} />
        </PaneSection>
      ) : (
        <PaneSection label="Not found">
          <p className={styles.hint}>That person doesn't exist, or was removed.</p>
        </PaneSection>
      )}
    </PaneFrame>
  );
}

function PersonBody({ user, canEdit }: { readonly user: UserDetail; readonly canEdit: boolean }): ReactNode {
  const session = useCurrentSession();
  const isSelf = session?.id === user.id;

  return (
    <>
      <StatusSection user={user} canEdit={canEdit} isSelf={isSelf} />
      {user.status === 'invited' && user.pendingInvite && <InviteSection user={user} canEdit={canEdit} />}

      <PaneEmbed>
        <RoleAssignmentEditor user={user} canEdit={canEdit} />
      </PaneEmbed>

      <OwnedSection user={user} />
      <DetailsSection user={user} canEdit={canEdit} />

      <PaneEmbed>
        <AvailabilityEditor userId={user.id} rows={user.availability} canEdit={canEdit || isSelf} />
      </PaneEmbed>

      <SessionsSection user={user} canEdit={canEdit} />
    </>
  );
}

// --- Status and actions -------------------------------------------------------

function StatusSection({
  user,
  canEdit,
  isSelf,
}: {
  readonly user: UserDetail;
  readonly canEdit: boolean;
  readonly isSelf: boolean;
}): ReactNode {
  const session = useCurrentSession();
  const showToast = useToasts((state) => state.show);
  const unlock = useUnlockUser(user.id);
  const reactivate = useReactivateUser(user.id);
  const suspend = useSuspendUser(user.id);
  const unsuspend = useUnsuspendUser(user.id);
  const reset = useSendPasswordReset(user.id);
  const {
    isOpen: isDeactivateOpen,
    anchor: deactivateAnchor,
    triggerRef: deactivateRef,
    toggle: toggleDeactivate,
    close: closeDeactivate,
  } = usePopover();
  const { isOpen: isViewAsOpen, anchor: viewAsAnchor, triggerRef: viewAsRef, toggle: toggleViewAs, close: closeViewAs } = usePopover();
  const { isOpen: isMoreOpen, anchor: moreAnchor, triggerRef: moreRef, toggle: toggleMore, close: closeMore } = usePopover();

  const status = listStatusOf(user);
  const locked = isLocked(user);
  const first = firstNameOf(user.fullName);
  // §L23 reserves viewing as somebody for Admin, and the server checks it again.
  const canViewAs =
    (session?.roles.includes('admin') ?? false) && !isSelf && !(session?.isImpersonating ?? false) && user.status === 'active';
  const canSuspend = canEdit && !isSelf && user.status === 'active';
  // The reset link needs a password to reset: not for an invite never accepted, nor a closed account.
  const canReset = canEdit && (user.status === 'active' || user.status === 'suspended');
  const canDeactivate = canEdit && !isSelf && user.status !== 'deactivated';

  const doSuspend = (): void => {
    closeMore();
    suspend.mutate(
      {},
      {
        onSuccess: () =>
          showToast({
            tone: 'success',
            message: `${user.fullName} suspended`,
            detail: "Signed out everywhere and can't sign in until unsuspended. Nothing of theirs moved.",
            action: { label: 'Undo', run: () => unsuspend.mutate() },
          }),
        onError: (error) => showToast({ tone: 'danger', message: error.message }),
      },
    );
  };

  const doReset = (): void => {
    closeMore();
    reset.mutate(undefined, {
      onSuccess: (result) =>
        showToast({
          tone: 'success',
          message: `Reset link sent to ${result.email}`,
          detail: 'They choose their own new password. You never see it.',
        }),
      onError: (error) => showToast({ tone: 'danger', message: error.message }),
    });
  };

  return (
    <PaneSection label="Status">
      <div className={styles.statusLine}>
        <ToneChip tone={PEOPLE_STATUS_TONES[status]}>{PEOPLE_STATUS_LABELS[status]}</ToneChip>
        <span className={styles.statusText}>{statusSentence(user)}</span>
      </div>

      {canEdit && (
        <div className={styles.actions}>
          {locked && (
            <Button
              variant="outline"
              size="sm"
              icon={KeyRound}
              onClick={() =>
                unlock.mutate(undefined, {
                  onSuccess: () => showToast({ tone: 'success', message: `${user.fullName} can sign in again` }),
                  onError: (error) => showToast({ tone: 'danger', message: error.message }),
                })
              }
            >
              Unlock
            </Button>
          )}
          {user.status === 'suspended' && (
            <Button
              variant="outline"
              size="sm"
              icon={PlayCircle}
              onClick={() =>
                unsuspend.mutate(undefined, {
                  onSuccess: () => showToast({ tone: 'success', message: `${user.fullName} can sign in again` }),
                  onError: (error) => showToast({ tone: 'danger', message: error.message }),
                })
              }
            >
              Unsuspend
            </Button>
          )}
          {user.status === 'deactivated' && (
            <Button
              variant="outline"
              size="sm"
              icon={UserCheck}
              onClick={() =>
                reactivate.mutate(undefined, {
                  onSuccess: (updated) =>
                    showToast({
                      tone: 'success',
                      message: `${user.fullName} reactivated`,
                      ...(updated.status === 'invited' ? { detail: 'They never accepted their invite, so a fresh one went out.' } : {}),
                    }),
                  onError: (error) => showToast({ tone: 'danger', message: error.message }),
                })
              }
            >
              Reactivate
            </Button>
          )}
          {canViewAs && (
            <Button ref={viewAsRef} variant="outline" size="sm" icon={Eye} onClick={toggleViewAs} aria-expanded={isViewAsOpen}>
              View as {first}
            </Button>
          )}
          {canDeactivate && (
            <Button
              ref={deactivateRef}
              variant="danger"
              size="sm"
              icon={UserX}
              onClick={toggleDeactivate}
              aria-expanded={isDeactivateOpen}
            >
              Deactivate…
            </Button>
          )}
          {(canSuspend || canReset) && (
            <Button
              ref={moreRef}
              variant="ghost"
              size="sm"
              icon={MoreHorizontal}
              aria-label={`More for ${user.fullName}`}
              aria-haspopup="menu"
              aria-expanded={isMoreOpen}
              onClick={toggleMore}
            />
          )}
        </div>
      )}

      <Popover anchor={deactivateAnchor} onClose={closeDeactivate} align="right" width={360} label={`Deactivate ${user.fullName}`}>
        <DeactivateConfirm user={user} onDone={closeDeactivate} onCancel={closeDeactivate} />
      </Popover>

      <Popover anchor={viewAsAnchor} onClose={closeViewAs} align="left" width={340} label={`View as ${user.fullName}`}>
        <ViewAsForm user={user} onCancel={closeViewAs} />
      </Popover>

      <Popover anchor={moreAnchor} onClose={closeMore} align="right" label={`More for ${user.fullName}`}>
        {canReset && (
          <MenuItem icon={<MailCheck size={16} aria-hidden />} onClick={doReset}>
            Send a password reset link
          </MenuItem>
        )}
        {canSuspend && (
          <MenuItem icon={<PauseCircle size={16} aria-hidden />} isDanger onClick={doSuspend}>
            Suspend
          </MenuItem>
        )}
      </Popover>
    </PaneSection>
  );
}

function statusSentence(user: UserDetail): string {
  if (isLocked(user)) return lockLine(user);
  if (user.status === 'suspended') return "Can't sign in until unsuspended.";
  if (user.status === 'deactivated') return "Can't sign in. Their work was handed over.";
  if (user.status === 'invited') return "Hasn't accepted the invite yet.";
  return user.lastLoginAt ? `Last signed in ${formatDateTime(user.lastLoginAt)}` : 'Has never signed in.';
}

/**
 * F1.10 — view the app as them. The reason is required and lands in the access log beside both names; it is
 * read-only and ends by itself after 30 minutes.
 */
function ViewAsForm({ user, onCancel }: { readonly user: UserDetail; readonly onCancel: () => void }): ReactNode {
  const start = useStartImpersonation();
  const showToast = useToasts((state) => state.show);
  const [reason, setReason] = useState('');
  const isReady = reason.trim().length >= 3;

  const submit = (): void => {
    if (!isReady) return;
    // On success the hook says so and goes home as them, which closes this with the page it sits on.
    start.mutate(
      { userId: user.id, reason: reason.trim() },
      { onError: (error) => showToast({ tone: 'danger', message: error.message }) },
    );
  };

  return (
    <div className={styles.popForm}>
      <h3 className={styles.popTitle}>View the app as {user.fullName}?</h3>
      <p className={styles.popBody}>
        You see exactly what they see. Nothing can be changed while you do, and it ends by itself after 30 minutes.
      </p>
      <Field label="Why" hint="Recorded in the access log beside both names." isRequired>
        {({ inputId, describedBy }) => (
          <Input
            id={inputId}
            aria-describedby={describedBy}
            value={reason}
            autoFocus
            placeholder="Checking why they can't see the Atlas jobs"
            onChange={(event) => setReason(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') submit();
            }}
          />
        )}
      </Field>
      <div className={styles.popActions}>
        <Button variant="quiet" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" icon={Eye} disabled={!isReady} isPending={start.isPending} onClick={submit}>
          Start viewing as them
        </Button>
      </div>
    </div>
  );
}

// --- Pending invite -------------------------------------------------------------

/**
 * F2.02 — the invite waiting to be accepted: send it again, or cancel it. The link can only be copied once it has
 * just been made (the server keeps a fingerprint of it, never the link), so Send again shows the new one with Copy.
 */
function InviteSection({ user, canEdit }: { readonly user: UserDetail; readonly canEdit: boolean }): ReactNode {
  const invite = user.pendingInvite;
  const resend = useResendInvite();
  const revoke = useRevokeInvite();
  const showToast = useToasts((state) => state.show);
  const [link, setLink] = useState<string | null>(null);
  const [hasCopied, setHasCopied] = useState(false);
  if (!invite) return null;

  const copy = (value: string): void => {
    void navigator.clipboard
      .writeText(value)
      .then(() => {
        setHasCopied(true);
        showToast({ tone: 'success', message: 'Invite link copied' });
      })
      .catch(() => showToast({ tone: 'danger', message: "The link wasn't copied", detail: 'Select the link and copy it yourself.' }));
  };

  return (
    <PaneSection title="Waiting to accept">
      <dl className={styles.facts}>
        <Fact label="Sent" value={formatDateTime(invite.createdAt)} />
        <Fact label="Sent by" value={orEmpty(invite.invitedByName)} />
        <Fact label="Link stops working" value={formatDateTime(invite.expiresAt)} />
      </dl>
      {canEdit && (
        <div className={styles.actions}>
          <Button
            variant="outline"
            size="sm"
            icon={RotateCw}
            isPending={resend.isPending}
            onClick={() =>
              resend.mutate(invite.id, {
                onSuccess: (result) => {
                  setLink(result.inviteUrl);
                  setHasCopied(false);
                  showToast({ tone: 'success', message: 'Invite sent again', detail: 'The previous link stopped working.' });
                },
                onError: (error) => showToast({ tone: 'danger', message: error.message }),
              })
            }
          >
            Send again
          </Button>
          {link && (
            <Button variant="outline" size="sm" icon={hasCopied ? Check : Copy} onClick={() => copy(link)}>
              {hasCopied ? 'Copied' : 'Copy link'}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            icon={Trash2}
            isPending={revoke.isPending}
            onClick={() =>
              revoke.mutate(invite.id, {
                onSuccess: () =>
                  showToast({ tone: 'success', message: 'Invite cancelled', detail: 'Reactivate them to send a fresh one.' }),
                onError: (error) => showToast({ tone: 'danger', message: error.message }),
              })
            }
          >
            Cancel invite
          </Button>
        </div>
      )}
      {link && <code className={styles.link}>{link}</code>}
    </PaneSection>
  );
}

// --- What they own ----------------------------------------------------------------

function OwnedSection({ user }: { readonly user: UserDetail }): ReactNode {
  return (
    <PaneSection title="What they own">
      <p className={styles.hint}>It moves to someone else before they can be deactivated.</p>
      <ul className={styles.owned}>
        {HANDOVER_KINDS.map((kind) => (
          <li key={kind} className={styles.ownedItem}>
            <span className={styles.ownedCount}>{formatNumber(user.ownedRecords[kind])}</span>
            <span className={styles.ownedLabel}>{HANDOVER_KIND_LABELS[kind]}</span>
          </li>
        ))}
      </ul>
    </PaneSection>
  );
}

// --- Details --------------------------------------------------------------------

function DetailsSection({ user, canEdit }: { readonly user: UserDetail; readonly canEdit: boolean }): ReactNode {
  const update = useUpdateUser(user.id);
  const showToast = useToasts((state) => state.show);

  return (
    <PaneSection title="Details">

      {canEdit && (
        <Field label="Full name" error={update.error?.fields.fullName}>
          {({ inputId, isInvalid }) => (
            <Input
              id={inputId}
              // A fresh box when the saved name changes, so an undone or refused edit shows the real name.
              key={user.fullName}
              defaultValue={user.fullName}
              isInvalid={isInvalid}
              onBlur={(event) => {
                const value = event.target.value.trim();
                if (value === user.fullName || value.length < 2) return;
                update.mutate(
                  { fullName: value },
                  {
                    onSuccess: () => showToast({ tone: 'success', message: 'Name updated' }),
                    onError: (error) => showToast({ tone: 'danger', message: error.message }),
                  },
                );
              }}
            />
          )}
        </Field>
      )}

      <dl className={styles.facts}>
        {!canEdit && <Fact label="Full name" value={user.fullName} />}
        <Fact label="Manager" value={orEmpty(user.managerName)} />
        <Fact label="Phone" value={orEmpty(user.phone)} />
        <Fact label="Timezone" value={user.timezone} />
        <Fact label="Two-factor" value={user.isTwoFactorEnabled ? 'Set up' : 'Not set up'} />
        <Fact label="Last signed in" value={user.lastLoginAt ? formatDateTime(user.lastLoginAt) : EMPTY} />
      </dl>

      <div className={styles.switchRow}>
        <span className={styles.switchText}>
          <span className={styles.switchLabel}>Sits on interview panels</span>
          <span className={styles.hint}>They can be picked to interview.</span>
        </span>
        <Switch
          ariaLabel="Sits on interview panels"
          checked={user.isPanelEligible}
          disabled={!canEdit}
          onChange={(checked) =>
            update.mutate(
              { isPanelEligible: checked },
              {
                onSuccess: () =>
                  showToast({ tone: 'success', message: checked ? 'Added to interview panels' : 'Removed from interview panels' }),
                onError: (error) => showToast({ tone: 'danger', message: error.message }),
              },
            )
          }
        />
      </div>

      <WorkCalendarOverride user={user} canEdit={canEdit} />
    </PaneSection>
  );
}

// --- Sessions -------------------------------------------------------------------

function SessionsSection({ user, canEdit }: { readonly user: UserDetail; readonly canEdit: boolean }): ReactNode {
  const revokeSession = useRevokeUserSession(user.id);
  const showToast = useToasts((state) => state.show);
  const [isAll, setIsAll] = useState(false);
  // The latest few, newest first; the rest on request (a busy account has dozens).
  const newestFirst = [...user.sessions].sort((a, b) => b.lastSeenAt.localeCompare(a.lastSeenAt));
  const shown = isAll ? newestFirst : newestFirst.slice(0, SESSIONS_SHOWN);

  return (
    <PaneSection
      label="Signed in on"
      title={
        <>
          Signed in on <span className={styles.count}>{formatNumber(user.sessions.length)}</span>
        </>
      }
    >
      {user.sessions.length === 0 ? (
        <p className={styles.hint}>Not signed in anywhere right now.</p>
      ) : (
        <ul className={styles.sessions}>
          {shown.map((row) => (
            <li key={row.id} className={styles.sessionRow}>
              <span className={styles.sessionText}>
                <span className={styles.sessionAgent}>
                  {describeAgent(row.userAgent)}
                  {row.isImpersonating ? ' · viewing as' : ''}
                </span>
                <span className={styles.sessionMeta}>
                  {orEmpty(row.ipAddress)} · {formatDateTime(row.lastSeenAt)}
                </span>
              </span>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon={LogOut}
                  aria-label="End this session"
                  title="End this session"
                  onClick={() =>
                    revokeSession.mutate(row.id, {
                      onSuccess: () => showToast({ tone: 'success', message: 'Session ended' }),
                      onError: (error) => showToast({ tone: 'danger', message: error.message }),
                    })
                  }
                />
              )}
            </li>
          ))}
        </ul>
      )}
      {user.sessions.length > SESSIONS_SHOWN && (
        <Button variant="ghost" size="sm" onClick={() => setIsAll((value) => !value)}>
          {isAll ? 'Show the latest only' : `Show all ${formatNumber(user.sessions.length)}`}
        </Button>
      )}
    </PaneSection>
  );
}

function Fact({ label, value }: { readonly label: string; readonly value: ReactNode }): ReactNode {
  return (
    <div className={styles.fact}>
      <dt className={styles.factLabel}>{label}</dt>
      <dd className={styles.factValue}>{value}</dd>
    </div>
  );
}
